import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import './AnnouncementBanner.scss';

const ALLOWED_TAGS = ['b', 'i', 'em', 'strong', 'a', 'br'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

function sanitize(content) {
  const withBreaks = content.replace(/\n/g, '<br>');
  return DOMPurify.sanitize(withBreaks, { ALLOWED_TAGS, ALLOWED_ATTR });
}

const DISMISSED_KEY = 'noiz_dismissed_announcements';

function getDismissedIds() {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
  } catch {
    return [];
  }
}

function dismissId(id) {
  try {
    const ids = getDismissedIds();
    if (!ids.includes(id)) {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids, id]));
    }
  } catch {}
}

class AnnouncementBanner extends Component {
  constructor(props) {
    super(props);
    this.state = { dismissed: false };
    this.resizeObserver = null;
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  handleBannerRef = (el) => {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    const { onHeightChange } = this.props;
    if (el && onHeightChange) {
      const report = () => onHeightChange(el.getBoundingClientRect().height);
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(report);
        this.resizeObserver.observe(el);
      }
      report();
    } else if (!el && onHeightChange) {
      onHeightChange(0);
    }
  };

  componentDidUpdate(prevProps) {
    const { announcement } = this.props;
    const prevAnnouncement = prevProps.announcement;
    if (announcement && announcement !== prevAnnouncement) {
      const ids = getDismissedIds();
      if (ids.includes(announcement.id)) {
        this.setState({ dismissed: true });
      } else {
        this.setState({ dismissed: false });
      }
    }
  }

  handleDismiss = () => {
    const { announcement, onDismiss } = this.props;
    if (announcement && announcement.id) {
      dismissId(announcement.id);
    }
    this.setState({ dismissed: true }, () => {
      if (onDismiss) onDismiss();
    });
  }

  render() {
    const { announcement, floating } = this.props;
    const { dismissed } = this.state;

    if (!announcement || dismissed) return null;

    const isDismissed = getDismissedIds().includes(announcement.id);
    if (isDismissed) return null;

    const bannerClass = classNames('AnnouncementBanner', { 'AnnouncementBanner--floating': floating });

    return (
      <div className={bannerClass} ref={this.handleBannerRef}>
        {/* eslint-disable-next-line react/no-danger */}
        <span className="AnnouncementBanner__message" dangerouslySetInnerHTML={{ __html: sanitize(announcement.messageContent) }} />
        <button
          className="AnnouncementBanner__dismiss"
          onClick={this.handleDismiss}
          type="button"
          aria-label="Dismiss announcement"
        >
          &times;
        </button>
      </div>
    );
  }
}

AnnouncementBanner.propTypes = {
  announcement: PropTypes.shape({
    id: PropTypes.string,
    messageContent: PropTypes.string,
  }),
  floating: PropTypes.bool,
  onDismiss: PropTypes.func,
  onHeightChange: PropTypes.func,
};

AnnouncementBanner.defaultProps = {
  announcement: null,
  floating: false,
  onDismiss: null,
  onHeightChange: null,
};

export default AnnouncementBanner;
